// ═══════════════════════════════════════════════
// Helpers de Comunicaciones — conversaciones y mensajes
// ═══════════════════════════════════════════════

import {
  addDoc, collection, doc, getDoc, getDocs, query, where,
  onSnapshot, serverTimestamp, updateDoc, orderBy, limit,
  deleteDoc, writeBatch,
} from 'firebase/firestore'
import { db } from './firebase'
import {
  COLLECTIONS,
  CONVERSACION_TIPOS,
  CONVERSACION_VISIBILIDAD,
  CONVERSACION_ESTADOS,
  MENSAJE_TIPOS,
  MENSAJE_PRIORIDADES,
  MENSAJE_PRIORIDAD_DEFAULT,
} from './constants'
import { forCreate, forUpdate, withTimestamps } from './dbHelpers'
import { docConId } from './docConId'

// ── Prioridad ──────────────────────────────────
/**
 * Devuelve la prioridad efectiva de un mensaje:
 *   mensaje.prioridad ?? conversacion.prioridad ?? 'normal'
 */
export function prioridadEfectiva(mensaje, conversacion) {
  if (mensaje?.prioridad) return mensaje.prioridad
  if (conversacion?.prioridad) return conversacion.prioridad
  return MENSAJE_PRIORIDAD_DEFAULT
}

const PRIORIDADES_VALIDAS = new Set(Object.values(MENSAJE_PRIORIDADES))
function validarPrioridad(p) {
  if (!p) return null
  if (!PRIORIDADES_VALIDAS.has(p)) throw new Error(`Prioridad inválida: ${p}`)
  return p
}

// ── Helpers de estado ──────────────────────────
/**
 * Devuelve el estado "efectivo" de una conversación incluso si todavía no tiene
 * el campo `estado` (conversaciones viejas creadas antes de esta funcionalidad).
 * Heurística para legacy: si tiene mensajes posteriores al creador → en_discusion,
 * si solo el creador escribió → enviado.
 */
export function estadoEfectivo(conv) {
  if (!conv) return CONVERSACION_ESTADOS.ENVIADO
  if (conv.estado) return conv.estado
  // Backfill: la conv antigua no tiene `estado`
  if (conv.ultimoMensajePor && conv.ultimoMensajePor !== conv.creadoPor) {
    return CONVERSACION_ESTADOS.EN_DISCUSION
  }
  return CONVERSACION_ESTADOS.ENVIADO
}

// ── Conversaciones ────────────────────────────

/**
 * Crea una nueva conversación.
 *
 * `empresaId` admite dos formas:
 *   - string (caso normal): la conversación queda anclada a esa empresa
 *     — patrón tenant-scoped clásico.
 *   - `null` EXPLÍCITO (Bloque 3 Módulo Tutoriales, 26-may-2026): conversación
 *     "interna Milbrands" sin contexto de empresa. Requiere que el caller tenga
 *     el permiso `mensajes.iniciar-interna` (gating client-side; las reglas
 *     Firestore validan que el creador sea `isConsultor()` como red de
 *     respaldo). Sirve para DMs entre staff que NO son sobre una empresa
 *     puntual (ej. compartir un tutorial). NO permitido para clientes.
 *
 * NOTA: si `empresaId === undefined` (no `null`), se sigue tratando como error
 * por el guard de `withEmpresaId` en `forCreate`. Para crear una conversación
 * interna hay que pasar `empresaId: null` explícitamente.
 *
 * @param {object} p
 * @param {string|null} p.empresaId
 * @param {string} p.creadoPor         userId
 * @param {string[]} p.participantes    userIds
 * @param {string} [p.titulo]           opcional (tema/asunto de la conversación)
 * @param {string} [p.tipo]             CONVERSACION_TIPOS.*
 * @param {string} [p.visibilidad]      PUBLICA | PRIVADA
 * @param {object} [p.contexto]         { tipo, recursoId, label } (plan, accionable, etc)
 * @param {string} [p.primerMensaje]    Si se pasa, se envía como el primer mensaje (burbuja)
 *                                       en cuanto se crea la conversación.
 * @param {string} [p.autorNombre]      Nombre del autor (para el primer mensaje)
 */
export async function crearConversacion(p) {
  const prioridadConv = validarPrioridad(p.prioridad) || MENSAJE_PRIORIDAD_DEFAULT
  const payload = {
    creadoPor: p.creadoPor,
    participantes: Array.from(new Set(p.participantes || [])).filter(Boolean),
    titulo: p.titulo || '',
    tipo: p.tipo || CONVERSACION_TIPOS.DIRECTA,
    visibilidad: p.visibilidad || CONVERSACION_VISIBILIDAD.PUBLICA,
    contexto: p.contexto || null,
    archivada: false,
    estado: CONVERSACION_ESTADOS.ENVIADO,
    prioridad: prioridadConv,
    cerradoEn: null,
    cerradoPor: null,
    ultimoMensajeEn: serverTimestamp(),
    ultimoMensajeTexto: '',
    ultimoMensajePor: '',
    ultimoMensajePrioridad: prioridadConv,
    // Contadores por usuario: {[uid]: N} mensajes no leídos
    noLeidos: {},
  }

  // Bypass intencional de `withEmpresaId` SOLO cuando empresaId es null explícito
  // (no undefined). `withEmpresaId` lanza para falsy values como barrera global
  // de tenant isolation; conviven dos casos legítimos: string (caso normal con
  // tenant) y null (conversación interna Milbrands del Bloque 3). Cualquier
  // otro valor falsy (undefined, '', 0) sigue rompiendo como antes.
  const data = p.empresaId === null
    ? withTimestamps({ ...payload, empresaId: null }, true)
    : forCreate(payload, p.empresaId)
  const ref = await addDoc(collection(db, COLLECTIONS.CONVERSACIONES), data)

  // Si vino un primer mensaje, lo enviamos como burbuja (no como título).
  // Fail-loud: si falla (ej. rules), NO lo tragamos en silencio — lo devolvemos
  // en `primerMensajeError` para que el caller avise en vez de dejar un hilo vacío.
  let primerMensajeError = null
  const primero = (p.primerMensaje || '').trim()
  if (primero) {
    try {
      await enviarMensaje({
        conversacionId: ref.id,
        autorId: p.creadoPor,
        autorNombre: p.autorNombre || '',
        texto: primero,
      })
    } catch (e) {
      console.error('crearConversacion → enviarMensaje (primer mensaje) falló:', e)
      primerMensajeError = e
    }
  }

  return { id: ref.id, ...data, primerMensajeError }
}

/** Suscribe a la lista de conversaciones donde participa userId, filtrado por empresaId.
 *  No usamos orderBy en el query para evitar requerir un índice compuesto;
 *  ordenamos en memoria por ultimoMensajeEn desc. */
export function subscribeConversaciones(userId, empresaId, cb) {
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES),
    where('participantes', 'array-contains', userId),
    where('empresaId', '==', empresaId)
  )
  return onSnapshot(q, snap => {
    const rows = snap.docs.map(docConId)
    rows.sort((a, b) => {
      const ta = a.ultimoMensajeEn?.toMillis?.() || 0
      const tb = b.ultimoMensajeEn?.toMillis?.() || 0
      return tb - ta
    })
    cb(rows)
  }, err => {
    console.error('subscribeConversaciones', err)
    cb([])
  })
}

/** Suscribe a TODAS las conversaciones en las que participa userId (todas las empresas).
 *  Usado por las notificaciones globales del portal (campanita de no leídos). */
export function subscribeTodasConversacionesUsuario(userId, cb) {
  if (!userId) { cb([]); return () => {} }
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES),
    where('participantes', 'array-contains', userId)
  )
  return onSnapshot(q, snap => {
    cb(snap.docs.map(docConId))
  }, err => {
    console.error('subscribeTodasConversacionesUsuario', err)
    cb([])
  })
}

/** Suscribe a mensajes de una conversación, orden cronológico ascendente. */
export function subscribeMensajes(conversacionId, cb) {
  // V3.7.5 — limit subido de 500 a 1000. Conversaciones largas (chats de
  // meses) trunca; lo correcto es paginación con `startAfter` al hacer scroll
  // hacia arriba, pero ese refactor es invasivo. 1000 cubre conversaciones
  // típicas. Si trunca, log warning para detectar el caso en producción.
  const QUERY_LIMIT = 1000
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES),
    orderBy('creadoEn', 'asc'),
    limit(QUERY_LIMIT)
  )
  return onSnapshot(q, snap => {
    if (snap.size >= QUERY_LIMIT) {
      console.warn(`[subscribeMensajes] conversación ${conversacionId} truncada a ${QUERY_LIMIT} mensajes — falta paginación`)
    }
    cb(snap.docs.map(docConId))
  }, err => {
    console.error('subscribeMensajes', err)
    cb([])
  })
}

/** Envía un mensaje de texto.
 *  Reglas de estado:
 *   - Si la conversación está CERRADA → no se permite enviar (lanza error).
 *   - Si el autor NO es el creador y el estado es 'enviado' o 'leido' → pasa a 'en_discusion'.
 *   - Si el autor ES el creador, el estado no avanza solo (queda como estaba).
 */
export async function enviarMensaje({ conversacionId, autorId, autorNombre, texto, tipo = MENSAJE_TIPOS.TEXTO, prioridad = null, visibilidad = null, destinatarios = null }) {
  const clean = (texto || '').trim()
  if (!clean) return null

  const prioridadMsg = validarPrioridad(prioridad)

  // 0) Validar que la conversación no esté cerrada
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const preSnap = await getDoc(convRef)
  let convData = null
  if (preSnap.exists()) {
    convData = preSnap.data()
    if (estadoEfectivo(convData) === CONVERSACION_ESTADOS.CERRADO) {
      const err = new Error('Esta conversación está cerrada. Reabrila para volver a escribir.')
      err.code = 'conversacion/cerrada'
      throw err
    }
  }

  // Prioridad efectiva del mensaje (solo guardo en el doc si el autor la seteó
  // explícitamente; sino la conv aporta el default al renderizar).
  const prioridadEfec = prioridadMsg || convData?.prioridad || MENSAJE_PRIORIDAD_DEFAULT

  // 1) Agregar mensaje en subcolección
  const msgPayload = {
    autorId,
    autorNombre: autorNombre || '',
    texto: clean,
    tipo,
    leidoPor: [autorId],     // el autor ya lo "leyó"
    creadoEn: serverTimestamp(),
  }
  if (prioridadMsg) msgPayload.prioridad = prioridadMsg
  // Visibilidad granular interno/compartido (Proyectos §7). Solo se sella si el
  // caller la pasa explícita; si no, el thread/recurso define el default visual.
  if (visibilidad) msgPayload.visibilidad = visibilidad
  // Destinatarios dirigidos (Proyectos Tanda 3, mismo patrón aditivo que
  // visibilidad): array de uids si el mensaje va dirigido; ausente = todos.
  if (Array.isArray(destinatarios) && destinatarios.length > 0) msgPayload.destinatarios = destinatarios

  const msgRef = await addDoc(
    collection(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES),
    msgPayload
  )

  // 2) Actualizar la conversación con snapshot del último mensaje + contadores + estado
  const convSnap = await getDoc(convRef)
  if (convSnap.exists()) {
    const conv = convSnap.data()
    const nextNoLeidos = { ...(conv.noLeidos || {}) }
    for (const uid of (conv.participantes || [])) {
      if (uid !== autorId) nextNoLeidos[uid] = (nextNoLeidos[uid] || 0) + 1
    }

    // Transición de estado: solo avanza si responde alguien que NO es el creador.
    const estadoActual = estadoEfectivo(conv)
    let nextEstado = estadoActual
    const esRespuestaDelOtro = autorId !== conv.creadoPor
    if (esRespuestaDelOtro && (estadoActual === CONVERSACION_ESTADOS.ENVIADO || estadoActual === CONVERSACION_ESTADOS.LEIDO)) {
      nextEstado = CONVERSACION_ESTADOS.EN_DISCUSION
    }

    const update = {
      ultimoMensajeEn: serverTimestamp(),
      ultimoMensajeTexto: clean.slice(0, 120),
      ultimoMensajePor: autorId,
      ultimoMensajePrioridad: prioridadEfec,
      noLeidos: nextNoLeidos,
    }
    if (nextEstado !== estadoActual || !conv.estado) {
      update.estado = nextEstado
    }
    // Si quien responde había prometido respuesta → limpiamos el compromiso.
    if (conv.compromisoPor && conv.compromisoPor === autorId) {
      update.compromisoEn = null
      update.compromisoPor = null
    }
    await updateDoc(convRef, forUpdate(update))
  }

  return msgRef.id
}

/** Marca como leída una conversación para un usuario (resetea contador).
 *  Además: si el lector NO es el creador y el estado está en 'enviado',
 *  avanza a 'leido' (= "el otro lo abrió pero no respondió todavía"). */
export async function marcarComoLeida(conversacionId, userId) {
  try {
    const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
    const convSnap = await getDoc(convRef)
    if (!convSnap.exists()) return
    const conv = convSnap.data()
    const noLeidos = { ...(conv.noLeidos || {}) }
    const update = {}
    if (noLeidos[userId]) {
      noLeidos[userId] = 0
      update.noLeidos = noLeidos
    }
    const estado = estadoEfectivo(conv)
    if (
      userId !== conv.creadoPor &&
      estado === CONVERSACION_ESTADOS.ENVIADO
    ) {
      update.estado = CONVERSACION_ESTADOS.LEIDO
    }
    if (Object.keys(update).length > 0) {
      await updateDoc(convRef, forUpdate(update))
    }
  } catch (e) { console.error('marcarComoLeida', e) }
}

/** Cierra una conversación (marca el "tema" como resuelto).
 *  Solo el creador (creadoPor) puede cerrar. Si otro lo intenta, lanza error. */
export async function cerrarConversacion(conversacionId, userId) {
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const snap = await getDoc(convRef)
  if (!snap.exists()) throw new Error('Conversación no encontrada')
  const conv = snap.data()
  // Pedido Mariano 17-jul: cualquier PARTICIPANTE (no solo el creador) puede
  // cerrar y archivar el tema — el cierre es global para todos.
  const esParticipante = Array.isArray(conv.participantes) && conv.participantes.includes(userId)
  if (conv.creadoPor !== userId && !esParticipante) {
    const err = new Error('Solo un participante del tema puede cerrarlo.')
    err.code = 'conversacion/no-autorizado'
    throw err
  }
  await updateDoc(convRef, forUpdate({
    estado: CONVERSACION_ESTADOS.CERRADO,
    cerradoEn: serverTimestamp(),
    cerradoPor: userId,
  }))
}

/** El receptor se compromete a responder en una fecha.
 *  Setea `compromisoEn` (Date) + `compromisoPor` (uid) sin tocar el estado principal,
 *  así puede convivir con LEIDO/NOTIFICADO. Cuando finalmente responde, el compromiso
 *  se limpia automáticamente en `enviarMensaje`. Solo puede llamarlo un no-creador. */
export async function comprometerRespuesta(conversacionId, userId, fecha) {
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const snap = await getDoc(convRef)
  if (!snap.exists()) throw new Error('Conversación no encontrada')
  const conv = snap.data()
  if (conv.creadoPor === userId) {
    const err = new Error('Solo el receptor puede comprometer una fecha de respuesta.')
    err.code = 'conversacion/no-autorizado'
    throw err
  }
  const fechaDate = fecha instanceof Date ? fecha : new Date(fecha)
  if (Number.isNaN(fechaDate.getTime())) throw new Error('Fecha inválida')
  await updateDoc(convRef, forUpdate({
    compromisoEn: fechaDate,
    compromisoPor: userId,
  }))
}

/** Limpia el compromiso de respuesta (porque ya respondió o porque se cancela). */
export async function limpiarCompromiso(conversacionId) {
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  await updateDoc(convRef, forUpdate({
    compromisoEn: null,
    compromisoPor: null,
  }))
}

/** Marca el tema como "notificado" (= "lo vi y tomé nota").
 *  Solo puede llamarlo un participante que NO sea el creador. No avanza si ya está
 *  EN_DISCUSION o CERRADO (no tiene sentido pisar esos estados). */
export async function marcarNotificado(conversacionId, userId) {
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const snap = await getDoc(convRef)
  if (!snap.exists()) throw new Error('Conversación no encontrada')
  const conv = snap.data()
  if (conv.creadoPor === userId) {
    const err = new Error('Solo el receptor puede marcar como notificado.')
    err.code = 'conversacion/no-autorizado'
    throw err
  }
  const estado = estadoEfectivo(conv)
  if (estado === CONVERSACION_ESTADOS.EN_DISCUSION || estado === CONVERSACION_ESTADOS.CERRADO) {
    return // no-op
  }
  await updateDoc(convRef, forUpdate({
    estado: CONVERSACION_ESTADOS.NOTIFICADO,
    notificadoEn: serverTimestamp(),
    notificadoPor: userId,
  }))
}

/** Reabre una conversación cerrada (vuelve al estado que tenía antes según mensajes). */
export async function reabrirConversacion(conversacionId, userId) {
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const snap = await getDoc(convRef)
  if (!snap.exists()) throw new Error('Conversación no encontrada')
  const conv = snap.data()
  // Igual que el cierre: cualquier participante puede reabrir (pedido 17-jul).
  const esParticipante = Array.isArray(conv.participantes) && conv.participantes.includes(userId)
  if (conv.creadoPor !== userId && !esParticipante) {
    const err = new Error('Solo un participante del tema puede reabrirlo.')
    err.code = 'conversacion/no-autorizado'
    throw err
  }
  // Determinar a qué estado vuelve: si hay respuestas del otro → en_discusion,
  // si solo habló el creador → enviado.
  const next = (conv.ultimoMensajePor && conv.ultimoMensajePor !== conv.creadoPor)
    ? CONVERSACION_ESTADOS.EN_DISCUSION
    : CONVERSACION_ESTADOS.ENVIADO
  await updateDoc(convRef, forUpdate({
    estado: next,
    cerradoEn: null,
    cerradoPor: null,
  }))
}

/** Agrega un userId al array leidoPor de un mensaje (para logs de lectura por mensaje). */
export async function marcarMensajeLeido(conversacionId, mensajeId, userId) {
  try {
    const ref = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return
    const d = snap.data()
    if ((d.leidoPor || []).includes(userId)) return
    await updateDoc(ref, { leidoPor: [...(d.leidoPor || []), userId] })
  } catch (e) { console.error('marcarMensajeLeido', e) }
}

/**
 * Encuentra o crea una conversación directa 1-a-1 entre dos userIds.
 *
 * `empresaId` admite las mismas dos formas que `crearConversacion`:
 *   - string: conversación normal anclada a esa empresa.
 *   - `null` EXPLÍCITO: conversación interna Milbrands (Bloque 3). Sirve para
 *     compartir tutoriales o cualquier DM entre staff sin contexto de empresa.
 *
 * Útil para "chatear con X" desde la vista de equipo, o "compartir tutorial
 * con Mateo" desde el modal de Compartir (Bloque 4).
 */
export async function getOrCreateDirecta({ empresaId, uidA, uidB, creadoPor }) {
  if (!uidA || !uidB || uidA === uidB) throw new Error('uids inválidos')
  // Usamos solo array-contains para evitar requerir índices compuestos.
  // Filtramos el resto en memoria.
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES),
    where('participantes', 'array-contains', uidA)
  )
  const snap = await getDocs(q)
  // Para empresaId === null (interna), buscamos conversaciones donde
  // empresaId también sea null/undefined; para string, match estricto.
  const matchEmpresa = (data) => empresaId === null
    ? !data.empresaId
    : data.empresaId === empresaId
  const existing = snap.docs.find(d => {
    const data = d.data()
    const p = data.participantes || []
    return matchEmpresa(data)
      && data.tipo === CONVERSACION_TIPOS.DIRECTA
      && p.length === 2
      && p.includes(uidB)
  })
  if (existing) return docConId(existing)
  return await crearConversacion({
    empresaId,
    creadoPor: creadoPor || uidA,
    participantes: [uidA, uidB],
    tipo: CONVERSACION_TIPOS.DIRECTA,
    visibilidad: CONVERSACION_VISIBILIDAD.PRIVADA,
  })
}

/**
 * Encuentra o crea la conversación contextual de un recurso (proyecto, tarea,
 * caso, etc). Patrón `contextoTipo` + `contextoId` del diseño de Proyectos §4.3:
 * cada recurso tiene UN thread asociado en Comunicaciones, creado lazy la primera
 * vez que alguien comenta. Reutiliza el campo `contexto` ya existente
 * (`{ tipo, recursoId, label }`) en vez de agregar campos top-level nuevos.
 *
 * El thread es PUBLICA (todos los participantes del recurso lo ven). La
 * visibilidad granular interno/compartido se aplica a nivel de cada mensaje
 * (campo `visibilidad` del mensaje), no del thread.
 *
 * @param {object} p
 * @param {string} p.empresaId      tenant dueño del recurso
 * @param {string} p.contextoTipo   'proyecto' | 'tarea' | 'caso' | ...
 * @param {string} p.contextoId     id del recurso
 * @param {string} p.creadoPor      userId que abre el thread
 * @param {string[]} [p.participantes]  uids con acceso (responsable + equipo)
 * @param {string} [p.label]        etiqueta legible (nombre del recurso)
 * @returns {Promise<{id:string}>}
 */
export async function getOrCreateConversacionContexto(p) {
  const { empresaId, contextoTipo, contextoId, creadoPor } = p
  if (!contextoTipo || !contextoId) throw new Error('contextoTipo + contextoId requeridos')
  // array-contains evita índice compuesto; el resto se filtra en memoria.
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES),
    where('participantes', 'array-contains', creadoPor),
  )
  const snap = await getDocs(q)
  const existing = snap.docs.find((d) => {
    const data = d.data()
    return data.contexto?.tipo === contextoTipo && data.contexto?.recursoId === contextoId
  })
  if (existing) return docConId(existing)
  return await crearConversacion({
    empresaId,
    creadoPor,
    participantes: Array.from(new Set([creadoPor, ...(p.participantes || [])])).filter(Boolean),
    tipo: CONVERSACION_TIPOS.CONTEXTO,
    visibilidad: CONVERSACION_VISIBILIDAD.PUBLICA,
    titulo: p.label || '',
    contexto: { tipo: contextoTipo, recursoId: contextoId, label: p.label || '' },
  })
}

/**
 * Suscribe a las conversaciones INTERNAS Milbrands del usuario (las que tienen
 * `empresaId: null`). Bloque 3 Módulo Tutoriales (26-may-2026).
 *
 * Devuelve unsubscribe. Cualquier autenticado puede llamar a la suscripción —
 * Firestore solo devuelve las conversaciones donde `userId` está en
 * `participantes` (por las reglas). El gating de quién puede CREAR conversaciones
 * internas vive en `crearConversacion` + reglas + UI client-side.
 *
 * Usa array-contains + where(empresaId, '==', null). Esto puede requerir un
 * índice compuesto la primera vez que se ejecuta — Firestore mostrará un link
 * en la consola para crearlo.
 *
 * @param {string} userId
 * @param {Function} cb
 * @returns {Function} unsubscribe
 */
export function subscribeConversacionesInternas(userId, cb) {
  if (!userId) { cb([]); return () => {} }
  const q = query(
    collection(db, COLLECTIONS.CONVERSACIONES),
    where('participantes', 'array-contains', userId),
    where('empresaId', '==', null),
  )
  return onSnapshot(q, snap => {
    const rows = snap.docs.map(docConId)
    rows.sort((a, b) => {
      const ta = a.ultimoMensajeEn?.toMillis?.() || 0
      const tb = b.ultimoMensajeEn?.toMillis?.() || 0
      return tb - ta
    })
    cb(rows)
  }, err => {
    console.error('subscribeConversacionesInternas', err)
    cb([])
  })
}

/** Borra un mensaje puntual. */
export async function borrarMensaje(conversacionId, mensajeId) {
  await deleteDoc(doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId))
}

/** Borra una conversación completa + todos sus mensajes. */
export async function borrarConversacion(conversacionId) {
  const msgsSnap = await getDocs(collection(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES))
  // Firestore batch limit: 500
  const docs = msgsSnap.docs
  for (let i = 0; i < docs.length; i += 400) {
    const batch = writeBatch(db)
    docs.slice(i, i + 400).forEach(d => batch.delete(d.ref))
    await batch.commit()
  }
  await deleteDoc(doc(db, COLLECTIONS.CONVERSACIONES, conversacionId))
}

/**
 * Cambia la prioridad "default" de una conversación. Los mensajes existentes
 * mantienen la suya (si la tenían explícita) o heredan la nueva conv.prioridad.
 * Puede llamarlo cualquier participante.
 */
export async function cambiarPrioridadConversacion(conversacionId, prioridad) {
  const valid = validarPrioridad(prioridad)
  if (!valid) throw new Error('Prioridad requerida')
  await updateDoc(doc(db, COLLECTIONS.CONVERSACIONES, conversacionId), forUpdate({
    prioridad: valid,
  }))
}

/**
 * Cambia la prioridad de un mensaje individual. Solo el autor debería llamarlo
 * (se valida en rules + en UI). Pasá `null` para quitar el override y volver al
 * default de la conversación.
 */
export async function cambiarPrioridadMensaje(conversacionId, mensajeId, prioridad) {
  const ref = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId)
  if (prioridad == null) {
    // Firestore no permite remover fácil, dejamos explícito en 'normal' si quieren "limpiar"
    // o aceptamos un valor explícito. Acá interpretamos null como "borrar el override":
    // la UI puede pedir confirm y setear MENSAJE_PRIORIDAD_DEFAULT.
    await updateDoc(ref, { prioridad: MENSAJE_PRIORIDAD_DEFAULT })
    return
  }
  const valid = validarPrioridad(prioridad)
  await updateDoc(ref, { prioridad: valid })
}

/** Resuelve datos mínimos de usuarios en bulk (para mostrar nombres/fotos en chat). */
export async function fetchUsuariosBasicos(uids) {
  const out = {}
  const unique = Array.from(new Set((uids || []).filter(Boolean)))
  for (const uid of unique) {
    try {
      const s = await getDoc(doc(db, COLLECTIONS.USUARIOS, uid))
      if (s.exists()) out[uid] = docConId(s)
    } catch {}
  }
  return out
}

// ── Edición de mensajes ────────────────────────

const VENTANA_EDICION_MS = 15 * 60 * 1000 // 15 minutos

/**
 * Edita un mensaje existente. Restricciones:
 * - Solo el autor puede editar.
 * - Ventana de 15 minutos desde creadoEn.
 * - Mensajes con acción completada son inmutables.
 * - Guarda historial de ediciones.
 */
export async function editarMensaje(conversacionId, mensajeId, userId, nuevoTexto) {
  const clean = (nuevoTexto || '').trim()
  if (!clean) throw new Error('El texto no puede estar vacío')

  const ref = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Mensaje no encontrado')

  const msg = snap.data()

  // Validar autoría
  if (msg.autorId !== userId) {
    const err = new Error('Solo el autor puede editar su mensaje.')
    err.code = 'mensaje/no-autorizado'
    throw err
  }

  // Validar ventana de tiempo
  const creadoMs = msg.creadoEn?.toMillis?.() || msg.creadoEn?.seconds * 1000 || 0
  if (Date.now() - creadoMs > VENTANA_EDICION_MS) {
    const err = new Error('El mensaje ya no se puede editar (pasaron más de 15 minutos).')
    err.code = 'mensaje/ventana-expirada'
    throw err
  }

  // Validar que no tenga acción completada
  if (msg.accion && ['aprobada', 'rechazada', 'expirada'].includes(msg.accion.estado)) {
    const err = new Error('Mensajes con acción completada no se pueden editar.')
    err.code = 'mensaje/inmutable'
    throw err
  }

  // Guardar historial
  const historial = [...(msg.historialEdiciones || [])]
  historial.push({
    textoAnterior: msg.texto,
    editadoEn: new Date().toISOString(),
  })

  await updateDoc(ref, {
    texto: clean,
    editado: true,
    historialEdiciones: historial,
    editadoEn: serverTimestamp(),
  })

  // Actualizar preview en conversación si era el último mensaje
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const convSnap = await getDoc(convRef)
  if (convSnap.exists()) {
    const conv = convSnap.data()
    if (conv.ultimoMensajePor === userId) {
      await updateDoc(convRef, { ultimoMensajeTexto: clean.slice(0, 120) })
    }
  }
}

/** Verifica si un mensaje aún puede editarse (para mostrar/ocultar botón en UI). */
export function puedeEditar(mensaje, userId) {
  if (!mensaje || mensaje.autorId !== userId) return false
  if (mensaje.eliminado) return false
  if (mensaje.accion && ['aprobada', 'rechazada', 'expirada'].includes(mensaje.accion?.estado)) return false
  const creadoMs = mensaje.creadoEn?.toMillis?.() || mensaje.creadoEn?.seconds * 1000 || 0
  return Date.now() - creadoMs <= VENTANA_EDICION_MS
}

// ── Adjuntos en mensajes ───────────────────────

/**
 * Envía un mensaje con adjuntos (archivos).
 * @param {object} p
 * @param {string} p.conversacionId
 * @param {string} p.autorId
 * @param {string} p.autorNombre
 * @param {string} [p.texto]                Texto opcional (puede ir solo adjuntos)
 * @param {Array<{nombre, url, tipo, tamano}>} p.adjuntos   Lista de adjuntos ya subidos a Storage
 * @param {string} [p.prioridad]
 */
export async function enviarMensajeConAdjuntos({ conversacionId, autorId, autorNombre, texto, adjuntos, prioridad = null }) {
  if ((!texto || !texto.trim()) && (!adjuntos || adjuntos.length === 0)) return null

  const prioridadMsg = validarPrioridad(prioridad)

  // Validar conversación no cerrada
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const preSnap = await getDoc(convRef)
  let convData = null
  if (preSnap.exists()) {
    convData = preSnap.data()
    if (estadoEfectivo(convData) === CONVERSACION_ESTADOS.CERRADO) {
      const err = new Error('Esta conversación está cerrada. Reabrila para volver a escribir.')
      err.code = 'conversacion/cerrada'
      throw err
    }
  }

  const prioridadEfec = prioridadMsg || convData?.prioridad || MENSAJE_PRIORIDAD_DEFAULT
  const cleanTexto = (texto || '').trim()

  const msgPayload = {
    autorId,
    autorNombre: autorNombre || '',
    texto: cleanTexto,
    tipo: adjuntos?.length > 0 ? 'adjunto' : MENSAJE_TIPOS.TEXTO,
    adjuntos: (adjuntos || []).map(a => ({
      nombre: a.nombre,
      url: a.url,
      tipo: a.tipo || 'application/octet-stream',
      tamano: a.tamano || 0,
    })),
    leidoPor: [autorId],
    creadoEn: serverTimestamp(),
  }
  if (prioridadMsg) msgPayload.prioridad = prioridadMsg

  const msgRef = await addDoc(
    collection(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES),
    msgPayload
  )

  // Actualizar conversación (mismo patrón que enviarMensaje)
  const convSnap = await getDoc(convRef)
  if (convSnap.exists()) {
    const conv = convSnap.data()
    const nextNoLeidos = { ...(conv.noLeidos || {}) }
    for (const uid of (conv.participantes || [])) {
      if (uid !== autorId) nextNoLeidos[uid] = (nextNoLeidos[uid] || 0) + 1
    }

    const estadoActual = estadoEfectivo(conv)
    let nextEstado = estadoActual
    const esRespuestaDelOtro = autorId !== conv.creadoPor
    if (esRespuestaDelOtro && (estadoActual === CONVERSACION_ESTADOS.ENVIADO || estadoActual === CONVERSACION_ESTADOS.LEIDO)) {
      nextEstado = CONVERSACION_ESTADOS.EN_DISCUSION
    }

    const previewText = cleanTexto
      ? cleanTexto.slice(0, 120)
      : `📎 ${adjuntos.map(a => a.nombre).join(', ').slice(0, 100)}`

    const update = {
      ultimoMensajeEn: serverTimestamp(),
      ultimoMensajeTexto: previewText,
      ultimoMensajePor: autorId,
      ultimoMensajePrioridad: prioridadEfec,
      noLeidos: nextNoLeidos,
    }
    if (nextEstado !== estadoActual || !conv.estado) update.estado = nextEstado
    if (conv.compromisoPor && conv.compromisoPor === autorId) {
      update.compromisoEn = null
      update.compromisoPor = null
    }
    await updateDoc(convRef, forUpdate(update))
  }

  return msgRef.id
}

// ── Soft delete de mensajes ────────────────────

/**
 * Soft-delete un mensaje (marca eliminado sin borrar físicamente).
 * Solo el autor o un master admin pueden ejecutarlo.
 * Mensajes con acción completada no se borran.
 */
export async function softDeleteMensaje(conversacionId, mensajeId, userId) {
  const ref = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Mensaje no encontrado')

  const msg = snap.data()

  // No borrar acciones completadas
  if (msg.accion && ['aprobada', 'rechazada', 'expirada'].includes(msg.accion?.estado)) {
    const err = new Error('Mensajes con acción completada no se pueden eliminar.')
    err.code = 'mensaje/inmutable'
    throw err
  }

  await updateDoc(ref, {
    eliminado: true,
    eliminadoEn: serverTimestamp(),
    eliminadoPor: userId,
  })
}

// ── Búsqueda de mensajes ───────────────────────

/**
 * Busca conversaciones por texto en título o último mensaje.
 * Búsqueda client-side sobre las conversaciones ya cargadas.
 * @param {Array} conversaciones - Array de conversaciones
 * @param {string} query - Texto a buscar
 * @returns {Array} Conversaciones filtradas
 */
export function buscarConversaciones(conversaciones, query) {
  if (!query || !query.trim()) return conversaciones
  const q = query.trim().toLowerCase()
  return conversaciones.filter(c => {
    const titulo = (c.titulo || '').toLowerCase()
    const ultimo = (c.ultimoMensajeTexto || '').toLowerCase()
    return titulo.includes(q) || ultimo.includes(q)
  })
}

// ── Acciones embebidas ──────────────────────────

/**
 * Tipos de acción embebida en mensajes.
 */
export const ACCION_TIPOS = {
  APROBACION: 'aprobacion',    // Aprobar/rechazar algo
  CONFIRMACION: 'confirmacion', // Confirmar algo (sí/no)
  FIRMA: 'firma',              // Firma digital simple
  ENCUESTA: 'encuesta',        // Opciones múltiples
}

export const ACCION_ESTADOS = {
  PENDIENTE: 'pendiente',
  APROBADA: 'aprobada',
  RECHAZADA: 'rechazada',
  CONFIRMADA: 'confirmada',
  EXPIRADA: 'expirada',
}

/**
 * Modos de aprobación.
 */
export const ACCION_MODOS = {
  SIMPLE: 'simple',           // Un solo aprobador
  CUALQUIERA: 'cualquiera',   // Cualquiera de los participantes
  TODOS: 'todos',             // Todos deben aprobar
}

/**
 * Envía un mensaje con acción embebida.
 * @param {object} p
 * @param {string} p.conversacionId
 * @param {string} p.autorId
 * @param {string} p.autorNombre
 * @param {string} p.texto - Descripción de la acción
 * @param {object} p.accion - { tipo, modo, titulo, descripcion, opciones?, expiraEn? }
 */
export async function enviarMensajeConAccion({ conversacionId, autorId, autorNombre, texto, accion, prioridad = null }) {
  if (!accion?.tipo) throw new Error('La acción requiere un tipo')
  if (!accion?.titulo) throw new Error('La acción requiere un título')

  const prioridadMsg = validarPrioridad(prioridad)

  // Validar conversación no cerrada
  const convRef = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId)
  const convSnap = await getDoc(convRef)
  if (!convSnap.exists()) throw new Error('Conversación no encontrada')
  if (convSnap.data().estado === CONVERSACION_ESTADOS.CERRADO) {
    throw new Error('No se puede enviar acciones a una conversación cerrada')
  }

  const accionData = {
    tipo: accion.tipo,
    modo: accion.modo || ACCION_MODOS.SIMPLE,
    titulo: accion.titulo,
    descripcion: accion.descripcion || '',
    estado: ACCION_ESTADOS.PENDIENTE,
    respuestas: {},         // { [userId]: { valor, fecha, nombre } }
    opciones: accion.opciones || null, // Para encuestas
    expiraEn: accion.expiraEn || null,
  }

  const msgRef = await addDoc(
    collection(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES),
    {
      tipo: MENSAJE_TIPOS.ACCION || 'accion',
      autorId,
      autorNombre: autorNombre || '',
      texto: (texto || '').trim(),
      accion: accionData,
      ...(prioridadMsg ? { prioridad: prioridadMsg } : {}),
      creadoEn: serverTimestamp(),
      leidoPor: [autorId],
    }
  )

  // Update conversación
  await updateDoc(convRef, {
    ultimoMensajeEn: serverTimestamp(),
    ultimoMensajeTexto: `[Acción] ${accion.titulo}`,
    ultimoMensajePor: autorId,
    actualizadoEn: serverTimestamp(),
  })

  return { id: msgRef.id }
}

/**
 * Responde a una acción embebida.
 * @param {string} conversacionId
 * @param {string} mensajeId
 * @param {string} userId
 * @param {string} userNombre
 * @param {string} valor - 'aprobada'|'rechazada'|'confirmada' o string libre (encuesta)
 * @param {string} [comentario]
 */
export async function responderAccion(conversacionId, mensajeId, userId, userNombre, valor, comentario = '') {
  const ref = doc(db, COLLECTIONS.CONVERSACIONES, conversacionId, COLLECTIONS.MENSAJES, mensajeId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Mensaje no encontrado')

  const msg = snap.data()
  if (!msg.accion) throw new Error('Este mensaje no tiene acción embebida')
  if (msg.accion.estado !== ACCION_ESTADOS.PENDIENTE) {
    throw new Error('Esta acción ya fue resuelta')
  }

  // Registrar respuesta
  const respuestas = { ...msg.accion.respuestas }
  respuestas[userId] = {
    valor,
    nombre: userNombre || '',
    comentario: comentario || '',
    fecha: new Date().toISOString(),
  }

  // Determinar nuevo estado según modo
  let nuevoEstado = ACCION_ESTADOS.PENDIENTE
  const modo = msg.accion.modo || ACCION_MODOS.SIMPLE

  if (modo === ACCION_MODOS.SIMPLE || modo === ACCION_MODOS.CUALQUIERA) {
    // Con una respuesta alcanza
    nuevoEstado = valor
  } else if (modo === ACCION_MODOS.TODOS) {
    // Necesita respuesta de todos los participantes de la conversación
    // Por ahora marcamos como resuelto solo si el valor no es pendiente
    nuevoEstado = valor
  }

  await updateDoc(ref, {
    'accion.respuestas': respuestas,
    'accion.estado': nuevoEstado,
    'accion.resueltoPor': userId,
    'accion.resueltoEn': serverTimestamp(),
  })

  return { nuevoEstado }
}

/**
 * Verifica si una acción está pendiente y no expirada.
 */
export function accionPendiente(accion) {
  if (!accion) return false
  if (accion.estado !== ACCION_ESTADOS.PENDIENTE) return false
  if (accion.expiraEn) {
    const exp = accion.expiraEn?.toDate?.() || new Date(accion.expiraEn)
    if (exp < new Date()) return false
  }
  return true
}

/**
 * Verifica si un usuario ya respondió a una acción.
 */
export function yaRespondio(accion, userId) {
  return !!accion?.respuestas?.[userId]
}
